import React, { useRef, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Paper, PaperProps, Fade, Slide, Typography } from '@material-ui/core';
import { Theme, makeStyles } from '@material-ui/core/styles';
import { ModalVariant } from 'providers/ModalProvider';
import ModalCloseButton from './ModalCloseButton';

const useStyles = makeStyles((theme: Theme) => {
  return {
    outerRoot: {
      display: 'flex',
      justifyContent: 'end',
      width: '100vw',
      height: '100vh',
      background: 'rgba(0, 0, 0, 0.7)',
      zIndex: theme.zIndex.modal,
      position: 'fixed',
      top: 0,
      left: 0
    },
    innerRoot: {
      position: 'relative',
      height: '100vh',
      borderRadius: 0,
      marginLeft: 'auto'
    },
    innerRootQuarter: {
      width: 500
    },
    innerRootHalf: {
      width: '65%' // Half-ish.
    },
    innerRootFull: {
      width: '95%',
      height: 'auto'
    },
    innerRootFloating: {
      width: 'auto',
      height: 'max-content',
      margin: 'auto'
    },
    container: {
      height: '100%'
    },
    header: {
      justifyContent: 'center',
      alignItems: 'center',
      display: 'flex',
      minHeight: 100,
      paddingLeft: theme.spacing(6)
    }
  };
});

export interface SlideModalProps {
  children: React.ReactNode;
  hideHeader?: boolean;
  onClose: () => void;
  open: boolean;
  title?: string;
  unmountOnClose?: boolean;
  variant: ModalVariant;
  paperProps?: PaperProps;
  containerClass?: string;
  hideCloseButton?: boolean;
}

const SlideModal = ({
  children,
  hideHeader,
  onClose,
  open,
  title,
  unmountOnClose,
  variant,
  paperProps,
  containerClass,
  hideCloseButton = false
}: SlideModalProps): React.ReactElement | null => {
  const classes = useStyles();

  const modal = useRef<HTMLDivElement | null>(null);
  const modalParent = useRef<HTMLDivElement | null>(null);
  const [tabList, setTabList] = useState<Array<Element> | null>(null);

  // This useEffect is required to run on every re-render of the component,
  // otherwise focus will not work properly.
  useEffect(() => {
    if (modal && modal.current) modal.current.focus();
  });

  useEffect(() => {
    if (modalParent.current) {
      // Gets all of the elements with tabindex set to keep track of all tabblable elements.
      // Sets it to state while casting to an Array instead of NodeList.
      setTabList(Array.prototype.slice.call(modalParent.current.querySelectorAll('[tabindex]')));
    }
  }, [modalParent]);

  useEffect(() => {
    const keyboardCallback = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab' || !tabList || !tabList.length) return;
      // Using a for-loop here to be able to break out of the loop.
      for (const [index, node] of tabList.entries()) {
        if (node.isSameNode(event.target as Node)) {
          if (!tabList[index + 1] && modal.current) {
            event.preventDefault();
            modal.current.focus();
            break;
          }
        }
      }
    };

    document.addEventListener('keydown', keyboardCallback);
    return (): void => {
      document.removeEventListener('keydown', keyboardCallback);
    };
  }, [tabList, onClose]);

  return (
    <Fade in={open} timeout={150}>
      <div className={classes.outerRoot}>
        <Slide direction="left" in={open} timeout={300} ref={modalParent}>
          {unmountOnClose && !open ? (
            <div />
          ) : (
            <Paper
              className={clsx(classes.innerRoot, {
                [classes.innerRootQuarter]: variant === ModalVariant.QUARTER,
                [classes.innerRootHalf]: variant === ModalVariant.HALF,
                [classes.innerRootFull]: variant === ModalVariant.FULL,
                [classes.innerRootFloating]: variant === ModalVariant.FLOATING
              })}
              ref={modal}
              {...paperProps}
            >
              {!hideCloseButton && (
                <ModalCloseButton
                  onClick={onClose}
                  tabIndex={0}
                  floatingModal={variant === ModalVariant.FLOATING}
                />
              )}
              <div className={clsx(classes.container, containerClass)}>
                {!hideHeader && (
                  <div className={classes.header}>
                    <Typography variant="h2" component="h1">
                      {title}
                    </Typography>
                  </div>
                )}
                {children}
              </div>
            </Paper>
          )}
        </Slide>
      </div>
    </Fade>
  );
};

export default SlideModal;
